<dx-popup [width]="1040" [height]="392" title="Make Transaction" [showTitle]="true"
   [closeOnOutsideClick]="false" [resizeEnabled]="true" [dragEnabled]="true"
   [visible]="isVisible" (onHidden)="onHidden()">
   <div *dxTemplate="let data of 'content'" class="mt-root">

      <!-- ============================ EDIT MODE (the HG leg-block clone) ============================
           (The former Direct-Commit price step is gone — acquisition prices are collected in the review
           pop-up's acquisition mode now.) -->
      <ng-container>

         <!-- Symbol picker (fixed). Drives the option chain + new-leg instrument composition. -->
         <div class="mt-symbol">
            <span class="mt-fld-label">Symbol</span>
            <!-- LOCKED once the underlying is determined (any bound leg exists) — owner rule 2026-06-10:
                 one underlying per transaction; changing the symbol mid-package would mix underlyings. -->
            <ets-symbol-picker [selectedInstrument]="underlying"
               [disabled]="underlyingLocked"
               (instrumentSelected)="onSymbolSelected($event)"></ets-symbol-picker>
         </div>

         <!-- Legs area (scrolls; ~6 rows). Each row = the HG hedge-pad single-leg block, cloned. -->
         <div class="mt-legs">

            <!-- Header row (clone of HG's header `.leg`). -->
            <div class="leg header">
               <!-- Ghost icon reserves the duplicate column's width (flex:0 = min-content). -->
               <div class="column duplicate"><i class="far fa-clone mt-ghost"></i></div>
               <div class="column kind">Kind</div>
               <div class="column buy-sell">Buy/Sell</div>
               <div class="column qty">Qty</div>
               <div class="column expiration">Expiration</div>
               <div class="column put-call">Call/Put</div>
               <div class="column strike">Strike</div>
               <div class="column group">Group</div>
               <div class="column live">Price</div>
               <!-- Ghost icon reserves the same width the leg rows' remove icon takes — otherwise the
                    header/totals price boxes sit ~11px right of the leg rows' (flex:0 = min-content). -->
               <div class="column remove"><i class="fa fa-times-circle mt-ghost"></i></div>
            </div>

            <!-- Leg rows scroll inside a dx-scroll-view (DevExtreme simulated scrollbar — overlay, no width
                 shift), matching HG. The header row above stays fixed. -->
            <dx-scroll-view class="mt-legs-scroll" [useNative]="false" showScrollbar="onHover">

            <!-- Existing (loaded, !isNew) legs first, then a separator, then the NEW legs. The ngFor is split
                 into two passes over orderedExistingLegs / orderedNewLegs so the separator can sit between the
                 two groups; it only shows when BOTH groups are non-empty. -->
            <ng-container *ngFor="let leg of orderedExistingLegs; let legIx = index">
            <!-- Solid separator between the asset cluster and the option legs (thinner than the old/new one). -->
            <div *ngIf="isAssetOptionBreak(orderedExistingLegs, legIx)" class="mt-asset-separator"></div>
            <!-- Thin separator between the option legs' UNDERLYING clusters (multi-underlying scopes). -->
            <div *ngIf="isUnderlyingBreak(orderedExistingLegs, legIx)" class="mt-ul-separator"></div>
            <div [class.mt-new]="leg.isNew" class="leg">

               <!-- Duplicate (Multi-Trade Pad parity): push a NEW editable copy of this leg. OPTIONS only —
                    the package holds at most ONE asset leg, so an asset row shows a width-keeping ghost. -->
               <div [class.removed]="leg.crossedOut" class="column duplicate">
                  <i *ngIf="isOption(leg)" (click)="onDuplicateLeg(leg)" class="far fa-clone" title="Duplicate leg"></i>
                  <i *ngIf="!isOption(leg)" class="far fa-clone mt-ghost"></i>
               </div>

               <!-- KIND (Asset/Option). -->
               <div [class.removed]="leg.crossedOut" class="column kind">{{ isOption(leg) ? 'Option' : 'Asset' }}</div>

               <!-- Buy/Sell — colored dx-select-box (clone of HG buy-sell column). -->
               <div [class.removed]="leg.crossedOut" class="column buy-sell">
                  <dx-select-box
                     (valueChange)="onLegSideChanging(leg, $event)"
                     [value]="sideText(leg.side)"
                     [disabled]="leg.crossedOut"
                     [inputAttr]="{style: sideStyle(leg.side)}"
                     [items]="sideTexts"
                     placeholder="Buy/Sell"
                  >
                     <div *dxTemplate="let data of 'item'">
                        <span [style.color]="data === 'Buy' ? 'cornflowerblue' : 'red'">{{ data }}</span>
                     </div>
                  </dx-select-box>
               </div>

               <!-- Qty — HG focus-split clone: the ORIGINAL qty (read-only once split) + a second box for
                    the DESIRED REMAINING qty. Focusing the original opens the split and auto-focuses the
                    desired box; an invalid desired qty reverts the split on focus-out. -->
               <div [class.removed]="leg.crossedOut" class="column qty">
                  <div class="owned">
                     <dx-number-box
                        (onFocusIn)="onQtyFocusIn(leg)"
                        [readOnly]="leg.qtySplit"
                        [disabled]="leg.crossedOut"
                        [inputAttr]="dxCenterTextStyleObj"
                        [min]="1"
                        [showSpinButtons]="false"
                        [value]="leg.originalQty"
                        placeholder="Qty"
                     ></dx-number-box>
                  </div>
                  <div *ngIf="leg.qtySplit" class="split">
                     <!-- valueChangeEvent="input": commit per keystroke, as the handler's contract
                          ("Split-box keystrokes") expects — the DX default 'change' commit left the
                          Review gate (hasTransaction reads transQty) stale until blur. The handler
                          stores invalid intermediates without toasting; focus-out settles them. -->
                     <dx-number-box
                        #transQtyBox
                        valueChangeEvent="input"
                        (onFocusOut)="onQtyFocusOut(leg)"
                        (onInitialized)="onTransQtyBoxInit(transQtyBox)"
                        (valueChange)="onTransQtyChange(leg, $event)"
                        [value]="leg.transQty"
                        [inputAttr]="dxCenterTextStyleObj"
                        [min]="0"
                        [showSpinButtons]="false"
                        placeholder="Qty"
                     ></dx-number-box>
                  </div>
               </div>

               <!-- Expiration — ets-expiration-selector for option legs; blank for asset legs.
                    Existing option legs use their OWN underlying's chain; new option legs use the picker's.
                    A strike/expiration change on an existing leg CLONES-AND-REMOVES (cross original + add a
                    normal new editable leg) exactly like HG. -->
               <div [class.removed]="leg.crossedOut" class="column expiration" [class.stock]="!isOption(leg)">
                  <ets-expiration-selector *ngIf="isOption(leg) && !leg.isNew"
                     (selectedExpirationChange)="onLegExpirationChanging(leg, $event)"
                     [selectedExpiration]="leg.expirationDescriptor"
                     [expirationDescriptors]="existingLegExpirations(leg)"
                     [inputAttr]="dxCenterTextStyleObj"
                     [showClearButton]="false"
                     [disabled]="leg.crossedOut || !leg.expirationDescriptor">
                  </ets-expiration-selector>
                  <ets-expiration-selector *ngIf="isOption(leg) && leg.isNew"
                     (selectedExpirationChange)="onExpiryChange(leg, $event)"
                     [selectedExpiration]="leg.expiration"
                     [expirationDescriptors]="legExpirations(leg)"
                     [inputAttr]="dxCenterTextStyleObj"
                     [showClearButton]="true"
                     [disabled]="leg.crossedOut || !legHasSymbol(leg)">
                  </ets-expiration-selector>
               </div>

               <!-- Call/Put — dx-select-box for option legs; blank for asset legs. EDITABLE: an existing-leg
                    type change routes through clone-and-remove (like strike/expiry). -->
               <div [class.removed]="leg.crossedOut" class="column put-call">
                  <dx-select-box *ngIf="isOption(leg)"
                     (valueChange)="onOptionTypeChange(leg, $event)"
                     [value]="leg.optionType"
                     [disabled]="leg.crossedOut"
                     [items]="optionTypeTexts"
                     [inputAttr]="dxCenterTextStyleObj"
                     placeholder="Call/Put"
                  ></dx-select-box>
               </div>

               <!-- Strike — ets-strikes-dropdown for option legs; blank for asset legs.
                    Existing legs resolve against their OWN expiration descriptor + underlying; new legs use
                    the symbol picker. A change on an existing leg CLONES-AND-REMOVES. New-leg strike is gated
                    on the leg having a chosen symbol AND expiration (same gate as the expiration). -->
               <div [class.removed]="leg.crossedOut" class="column strike">
                  <ets-strikes-dropdown *ngIf="isOption(leg) && !leg.isNew"
                     (strikeChange)="onExistingStrikeChange(leg, $event)"
                     [strike]="leg.strike"
                     [expiration]="leg.expirationDescriptor"
                     [targetInstrument]="existingLegInstrument(leg)"
                     [inputAttr]="dxCenterTextStyleObj"
                     [showAtm]="false"
                     [disable]="leg.crossedOut || !leg.expirationDescriptor">
                  </ets-strikes-dropdown>
                  <ets-strikes-dropdown *ngIf="isOption(leg) && leg.isNew"
                     (strikeChange)="onStrikeChange(leg, $event)"
                     [strike]="leg.strike"
                     [expiration]="leg.expiration"
                     [targetInstrument]="underlying"
                     [inputAttr]="dxCenterTextStyleObj"
                     [showAtm]="true"
                     [disable]="leg.crossedOut || !legHasSymbol(leg)">
                  </ets-strikes-dropdown>
               </div>

               <!-- Target Group — READ-ONLY on existing legs (owner ruling 2026-06-10): a held position's
                    group is a fact; only NEW legs route via the picker. -->
               <div [class.removed]="leg.crossedOut" class="column group">
                  <dx-select-box
                     [items]="routingTargetGroups"
                     valueExpr="bucketId"
                     displayExpr="name"
                     [value]="leg.groupBucketId"
                     [readOnly]="true"
                     [disabled]="leg.crossedOut"
                     [inputAttr]="dxCenterTextStyleObj"
                     placeholder="Group">
                  </dx-select-box>
               </div>

               <!-- Price — HG dual-pricebox clone. OWN = the leg as if owned (sign/colour = the closing
                    action); TRANS = the derived transaction's direct price. The TRANS sub-column renders
                    only when ANY transaction exists; per-leg cells hide via visibility so rows stay
                    aligned. Crossed legs price in TRANS (their close), new legs own nothing. -->
               <div [class.removed]="leg.crossedOut" class="column live">
                  <div class="owned" [style.visibility]="ownCellVisible(leg) ? 'visible' : 'hidden'">
                     <dx-number-box #pxOwn
                        [readOnly]="true"
                        [value]="ownPriceOf(leg)"
                        format="$,##0.00"
                        [inputAttr]="priceBoxStyle(pxOwn.value)"
                     ></dx-number-box>
                  </div>
                  <div *ngIf="transColumnVisible" class="trans"
                       [style.visibility]="transCellVisible(leg) ? 'visible' : 'hidden'">
                     <dx-number-box #pxTrans
                        [readOnly]="true"
                        [value]="transPriceOf(leg)"
                        format="$,##0.00"
                        [inputAttr]="priceBoxStyle(pxTrans.value)"
                     ></dx-number-box>
                  </div>
               </div>

               <!-- Remove / restore (clone of HG fa fa-times-circle / fa fa-undo). -->
               <div class="column remove">
                  <i (click)="onCrossOut(leg)" *ngIf="!leg.crossedOut" class="fa fa-times-circle"></i>
                  <i (click)="onRestore(leg)" *ngIf="leg.crossedOut && leg.restorable" class="fa fa-undo"></i>
               </div>

            </div>
            </ng-container>

            <!-- Divider between existing legs and new legs. Only when BOTH groups are non-empty. -->
            <div *ngIf="orderedExistingLegs.length && orderedNewLegs.length" class="mt-legs-separator"></div>

            <!-- NEW (isNew) legs. EVERY leg is fully editable. -->
            <ng-container *ngFor="let leg of orderedNewLegs; let legIx = index">
            <!-- Solid separator between the asset cluster and the option legs (thinner than the old/new one). -->
            <div *ngIf="isAssetOptionBreak(orderedNewLegs, legIx)" class="mt-asset-separator"></div>
            <!-- Thin separator between the option legs' UNDERLYING clusters (multi-underlying scopes). -->
            <div *ngIf="isUnderlyingBreak(orderedNewLegs, legIx)" class="mt-ul-separator"></div>
            <div [class.mt-new]="leg.isNew" class="leg">

               <!-- Duplicate (Multi-Trade Pad parity): push a NEW editable copy of this leg. OPTIONS only —
                    the package holds at most ONE asset leg, so an asset row shows a width-keeping ghost. -->
               <div [class.removed]="leg.crossedOut" class="column duplicate">
                  <i *ngIf="isOption(leg)" (click)="onDuplicateLeg(leg)" class="far fa-clone" title="Duplicate leg"></i>
                  <i *ngIf="!isOption(leg)" class="far fa-clone mt-ghost"></i>
               </div>

               <!-- KIND (Asset/Option). -->
               <div [class.removed]="leg.crossedOut" class="column kind">{{ isOption(leg) ? 'Option' : 'Asset' }}</div>

               <!-- Buy/Sell — colored dx-select-box (clone of HG buy-sell column). -->
               <div [class.removed]="leg.crossedOut" class="column buy-sell">
                  <dx-select-box
                     (valueChange)="onLegSideChanging(leg, $event)"
                     [value]="sideText(leg.side)"
                     [disabled]="leg.crossedOut"
                     [inputAttr]="{style: sideStyle(leg.side)}"
                     [items]="sideTexts"
                     placeholder="Buy/Sell"
                  >
                     <div *dxTemplate="let data of 'item'">
                        <span [style.color]="data === 'Buy' ? 'cornflowerblue' : 'red'">{{ data }}</span>
                     </div>
                  </dx-select-box>
               </div>

               <!-- Qty — centered number box (clone of HG qty column). -->
               <div [class.removed]="leg.crossedOut" class="column qty">
                  <div class="owned">
                     <dx-number-box
                        (valueChange)="onTargetQtyChange(leg, $event)"
                        [disabled]="leg.crossedOut"
                        [inputAttr]="dxCenterTextStyleObj"
                        [min]="1"
                        [showSpinButtons]="false"
                        [value]="leg.targetQty"
                        placeholder="Qty"
                     ></dx-number-box>
                  </div>
               </div>

               <!-- Expiration — ets-expiration-selector for option legs; blank for asset legs.
                    New option legs use the picker's chain. -->
               <div [class.removed]="leg.crossedOut" class="column expiration" [class.stock]="!isOption(leg)">
                  <ets-expiration-selector *ngIf="isOption(leg)"
                     (selectedExpirationChange)="onExpiryChange(leg, $event)"
                     [selectedExpiration]="leg.expiration"
                     [expirationDescriptors]="legExpirations(leg)"
                     [inputAttr]="dxCenterTextStyleObj"
                     [showClearButton]="true"
                     [disabled]="leg.crossedOut || !legHasSymbol(leg)">
                  </ets-expiration-selector>
               </div>

               <!-- Call/Put — dx-select-box for option legs; blank for asset legs. EDITABLE in place. -->
               <div [class.removed]="leg.crossedOut" class="column put-call">
                  <dx-select-box *ngIf="isOption(leg)"
                     (valueChange)="onOptionTypeChange(leg, $event)"
                     [value]="leg.optionType"
                     [disabled]="leg.crossedOut"
                     [items]="optionTypeTexts"
                     [inputAttr]="dxCenterTextStyleObj"
                     placeholder="Call/Put"
                  ></dx-select-box>
               </div>

               <!-- Strike — ets-strikes-dropdown for option legs; blank for asset legs.
                    New legs use the symbol picker; gated on a chosen symbol AND expiration. -->
               <div [class.removed]="leg.crossedOut" class="column strike">
                  <ets-strikes-dropdown *ngIf="isOption(leg)"
                     (strikeChange)="onStrikeChange(leg, $event)"
                     [strike]="leg.strike"
                     [expiration]="leg.expiration"
                     [targetInstrument]="underlying"
                     [inputAttr]="dxCenterTextStyleObj"
                     [showAtm]="true"
                     [disable]="leg.crossedOut || !legHasSymbol(leg)">
                  </ets-strikes-dropdown>
               </div>

               <!-- Target Group — dx-select-box over routingTargetGroups (only groups hold positions). -->
               <div [class.removed]="leg.crossedOut" class="column group">
                  <dx-select-box
                     (valueChange)="onLegGroupChange(leg, $event)"
                     [items]="routingTargetGroups"
                     valueExpr="bucketId"
                     displayExpr="name"
                     [value]="leg.groupBucketId"
                     [disabled]="leg.crossedOut"
                     [inputAttr]="dxCenterTextStyleObj"
                     placeholder="Group">
                  </dx-select-box>
               </div>

               <!-- Price — HG dual-pricebox clone. OWN = the leg as if owned (sign/colour = the closing
                    action); TRANS = the derived transaction's direct price. The TRANS sub-column renders
                    only when ANY transaction exists; per-leg cells hide via visibility so rows stay
                    aligned. Crossed legs price in TRANS (their close), new legs own nothing. -->
               <div [class.removed]="leg.crossedOut" class="column live">
                  <div class="owned" [style.visibility]="ownCellVisible(leg) ? 'visible' : 'hidden'">
                     <dx-number-box #pxOwn
                        [readOnly]="true"
                        [value]="ownPriceOf(leg)"
                        format="$,##0.00"
                        [inputAttr]="priceBoxStyle(pxOwn.value)"
                     ></dx-number-box>
                  </div>
                  <div *ngIf="transColumnVisible" class="trans"
                       [style.visibility]="transCellVisible(leg) ? 'visible' : 'hidden'">
                     <dx-number-box #pxTrans
                        [readOnly]="true"
                        [value]="transPriceOf(leg)"
                        format="$,##0.00"
                        [inputAttr]="priceBoxStyle(pxTrans.value)"
                     ></dx-number-box>
                  </div>
               </div>

               <!-- Remove / restore (clone of HG fa fa-times-circle / fa fa-undo). -->
               <div class="column remove">
                  <i (click)="onCrossOut(leg)" *ngIf="!leg.crossedOut" class="fa fa-times-circle"></i>
                  <i (click)="onRestore(leg)" *ngIf="leg.crossedOut && leg.restorable" class="fa fa-undo"></i>
               </div>

            </div>
            </ng-container>

            <div *ngIf="!editableLegs.length" class="mt-empty">
               No current positions in this scope. Add a leg below.
            </div>

            </dx-scroll-view>

            <!-- Totals row (HG group-totals clone): OWN structure total always; TRANS total appears with
                 the trans column. Normalized by the combo's Default Qty; OPTION legs only (owner decision —
                 asset legs are excluded from totals). Hidden when the scope spans multiple combos. -->
            <div *ngIf="totalsVisible" class="leg total mt-totals">
               <!-- Ghost icon = same width as the leg rows' duplicate icon (columns align). -->
               <div class="column duplicate"><i class="far fa-clone mt-ghost"></i></div>
               <div class="column kind"></div>
               <div class="column buy-sell"></div>
               <div class="column qty"></div>
               <div class="column expiration"></div>
               <div class="column put-call"></div>
               <div class="column strike"></div>
               <div class="column group mt-totals-label">Total</div>
               <div class="column live">
                  <div class="owned">
                     <dx-number-box #totalOwn
                        [readOnly]="true"
                        [value]="ownTotal"
                        format="$,##0.00"
                        [inputAttr]="priceBoxStyle(totalOwn.value)"
                     ></dx-number-box>
                  </div>
                  <div *ngIf="transColumnVisible" class="trans">
                     <dx-number-box #totalTrans
                        [readOnly]="true"
                        [value]="transTotal"
                        format="$,##0.00"
                        [inputAttr]="priceBoxStyle(totalTrans.value)"
                     ></dx-number-box>
                  </div>
               </div>
               <!-- Ghost icon = same width as the leg rows' remove icon (price boxes align). -->
               <div class="column remove"><i class="fa fa-times-circle mt-ghost"></i></div>
            </div>

         </div>

         <!-- Footer (sticky). LEFT = Add Option / Add Asset; RIGHT = the action buttons. -->
         <div class="mt-footer">
            <div class="mt-footer-left">
               <dx-button text="Add Option" icon="plus" [disabled]="busy" (onClick)="onAddOption()"></dx-button>
               <dx-button text="Add Asset" icon="plus" [disabled]="busy || !canAddAsset" (onClick)="onAddAsset()"></dx-button>
            </div>
            <!-- Save + Copy-to-TOS moved INTO the preview (owner workflow 2026-06-10): design -> Review ->
                 Copy to TOS (writes clipboard AND saves the end-state) or Close. Button order/wording per
                 owner: Review, Acquire (the former Direct Commit; stays red), Cancel — equal sizes. -->
            <!-- Acquire lives INSIDE the review pop-up now (owner workflow 2026-06-10): the dialog offers
                 only Review + Cancel. Review is disabled while the edit set derives NO transaction. -->
            <div class="mt-footer-right">
               <dx-button text="Review" type="default" width="110" [disabled]="busy || !hasTransaction" (onClick)="onPreview()"></dx-button>
               <dx-button text="Cancel" width="110" [disabled]="busy" (onClick)="onCancel()"></dx-button>
            </div>
         </div>

      </ng-container>

   </div>
</dx-popup>

<!-- Separate, price-free Before / Transaction / After preview pop-up (clone of hg-after-state-preview). -->
<ets-collars-after-state-preview></ets-collars-after-state-preview>
